import { useCallback, useEffect, useRef, useState } from 'react';
import type { UploadPhase, UploadProgress, UploadProgressHandler } from '../lib/fileUpload';

/**
 * Tracks files that are currently being uploaded, so the UI can show what is
 * happening instead of a bare spinner.
 *
 * One queue serves every upload surface: resource tabs, onboarding, meeting
 * notes, bank statements, comment composers. The caller keeps its own upload
 * logic — this only owns the per-file progress state and hands out the
 * `onProgress` callback to feed it.
 */

export interface UploadTask {
  id: string;
  /** File name, shown to the person so they can tell several uploads apart. */
  name: string;
  /** Total size in bytes, from the File object. */
  size: number;
  loaded: number;
  total: number;
  /** 0–100; held at 99 until the last byte is out (see lib/fileUpload). */
  percent: number;
  phase: UploadPhase;
}

let nextId = 0;

export interface UploadQueue {
  /** In-flight uploads, oldest first. Empty when nothing is running. */
  uploads: UploadTask[];
  /** True while at least one upload is in flight. */
  isUploading: boolean;
  /**
   * Run one file's upload while tracking it.
   *
   * `work` receives the progress callback to pass down to the transport
   * (`putFileWithProgress`, `uploadViaSignedUrl`, …). The task stays in the
   * list until `work` settles — which includes any follow-up call the caller
   * makes, such as saving the resource row — and is removed either way, so a
   * failed upload leaves no ghost row behind. Errors propagate unchanged;
   * callers already render their own message.
   */
  run: <T>(file: File, work: (onProgress: UploadProgressHandler) => Promise<T>) => Promise<T>;
}

export function useUploadQueue(): UploadQueue {
  const [uploads, setUploads] = useState<UploadTask[]>([]);
  // Progress events can outlive the component (a person navigating away mid
  // upload); the guard keeps React from warning about a dead setState.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async <T>(file: File, work: (onProgress: UploadProgressHandler) => Promise<T>): Promise<T> => {
      const id = `upload-${(nextId += 1)}`;
      const task: UploadTask = {
        id,
        name: file.name,
        size: file.size,
        loaded: 0,
        total: file.size,
        percent: 0,
        phase: 'uploading',
      };
      setUploads((prev) => [...prev, task]);

      const onProgress = (progress: UploadProgress) => {
        if (!mounted.current) return;
        setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...progress } : u)));
      };

      try {
        return await work(onProgress);
      } finally {
        if (mounted.current) setUploads((prev) => prev.filter((u) => u.id !== id));
      }
    },
    [],
  );

  return { uploads, isUploading: uploads.length > 0, run };
}
